import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Package, Truck, X, CheckCircle2, Circle, Receipt } from 'lucide-react';
import { useAlert } from '../context/AlertContext';
import API_BASE_URL from '../config/api';
import InvoiceView from '../components/InvoiceView';

const OrderProductImage = ({ src, alt }) => {
    const [error, setError] = useState(false);

    if (error || !src) {
        return <Package size={24} className="text-gray-300" />;
    }

    return (
        <img
            src={src}
            alt={alt}
            className="h-full w-full object-contain p-2"
            onError={() => setError(true)}
        />
    );
};

const ProductOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [viewInvoiceOrderId, setViewInvoiceOrderId] = useState(null);
    const { showAlert } = useAlert();

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOrders = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/shop/my_orders.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setOrders(data.data);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            showAlert('Failed to load orders', 'error');
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'Approved': return 'text-blue-600 bg-blue-50 border-blue-200';
            case 'Ready to Dispatch': return 'text-purple-600 bg-purple-50 border-purple-200';
            case 'Shipped': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
            case 'Delivered': return 'text-green-600 bg-green-50 border-green-200';
            case 'Cancelled': return 'text-red-600 bg-red-50 border-red-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const StatusTimeline = ({ currentStatus }) => {
        const statuses = [
            { label: 'Pending', key: 'Pending' },
            { label: 'Approved', key: 'Approved' },
            { label: 'Ready to Dispatch', key: 'Ready to Dispatch' },
            { label: 'Shipped', key: 'Shipped' },
            { label: 'Delivered', key: 'Delivered' }
        ];

        const currentIndex = statuses.findIndex(s => s.key === currentStatus);

        if (currentStatus === 'Cancelled') {
            return (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
                    <X className="bg-red-600 text-white rounded-full p-1" size={24} />
                    <div>
                        <p className="font-bold">Order Cancelled</p>
                        <p className="text-sm">This order has been cancelled and will not be processed further.</p>
                    </div>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                {statuses.map((s, idx) => {
                    const isCompleted = idx < currentIndex;
                    const isCurrent = idx === currentIndex;
                    const isLast = idx === statuses.length - 1;

                    return (
                        <div key={s.key} className="relative flex gap-4">
                            {!isLast && (
                                <div className={`absolute left-3 top-6 w-0.5 h-10 ${isCompleted ? 'bg-green-500' : 'bg-gray-200'}`} />
                            )}
                            <div className="z-10">
                                {isCompleted ? (
                                    <CheckCircle2 className="text-green-500 bg-white" size={24} />
                                ) : isCurrent ? (
                                    <div className="h-6 w-6 rounded-full border-4 border-blue-600 bg-white flex items-center justify-center animate-pulse" />
                                ) : (
                                    <Circle className="text-gray-300 bg-white" size={24} />
                                )}
                            </div>
                            <div className="pb-4">
                                <p className={`font-bold ${isCurrent ? 'text-blue-600' : isCompleted ? 'text-gray-900' : 'text-gray-400'}`}>
                                    {s.label}
                                </p>
                                {isCurrent && (
                                    <p className="text-xs text-gray-500 mt-0.5 tracking-tight">Current status of your package</p>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const processOrders = () => {
        const grouped = {};

        orders.forEach(order => {
            // Group by creation time (same minute) and shipping address
            const date = new Date(order.created_at);
            const timeKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}-${date.getHours()}-${date.getMinutes()}`;
            const key = `${timeKey}-${order.shipping_address}-${order.shipping_zip}`;

            if (!grouped[key]) {
                grouped[key] = {
                    ...order,
                    id: `GRP-${order.id}`,
                    original_orders: [order],
                    total_amount: parseFloat(order.total_amount),
                    items: [...order.items],
                    order_count: 1,
                    // Track if statuses vary
                    all_statuses: [order.status]
                };
            } else {
                grouped[key].total_amount += parseFloat(order.total_amount);
                grouped[key].items = [...grouped[key].items, ...order.items];
                grouped[key].order_count += 1;
                grouped[key].all_statuses.push(order.status);

                // If statuses are different, we can show a summary or the "lowest" one
                // For now, let's keep the most recent order's status as indicative
            }
        });

        return Object.values(grouped);
    };

    const groupedOrders = processOrders();

    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center gap-3">
                            <Package className="text-blue-600" size={32} />
                            My Orders
                        </h1>
                        <p className="text-gray-500 font-medium tracking-tight">Track and manage your consolidated shopping history</p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                    </div>
                ) : groupedOrders.length > 0 ? (
                    <div className="space-y-6">
                        {groupedOrders.map(order => (
                            <div key={order.id} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-gray-100 transition-all duration-300">
                                <div className="p-5 sm:p-6 border-b border-gray-50 flex flex-col md:flex-row justify-between md:items-center gap-4 bg-gray-50/50">
                                    <div className="flex-1">
                                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                                            <h3 className="font-black text-lg text-gray-900">
                                                {order.order_count > 1 ? `Grouped Order (${order.order_count} Pkgs)` : order.order_unique_id}
                                            </h3>
                                            <span className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border ${getStatusColor(order.status)}`}>
                                                {order.status}
                                            </span>
                                            {order.order_count > 1 && (
                                                <span className="px-3 py-1 bg-indigo-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-100">
                                                    Combined
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500 font-bold uppercase tracking-wider">
                                            <span>📅 Placed on {new Date(order.created_at).toLocaleDateString()}</span>
                                            <span className="hidden sm:inline">•</span>
                                            <span className="hidden sm:inline">📦 {order.items.length} Items</span>
                                        </div>
                                    </div>
                                    <div className="flex flex-row md:flex-col items-center md:items-end justify-between gap-3 border-t md:border-t-0 pt-4 md:pt-0">
                                        <div className="text-2xl font-black text-blue-600">
                                            ₹{parseFloat(order.total_amount).toLocaleString()}
                                        </div>
                                        <div className="flex flex-wrap gap-2">
                                            {['Approved', 'Ready to Dispatch', 'Shipped', 'Delivered'].includes(order.status) && (
                                                <button
                                                    onClick={() => setViewInvoiceOrderId(order.original_orders[0].id)}
                                                    className="px-4 py-2 bg-blue-50 text-blue-700 border border-blue-200 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-blue-100 transition-all flex items-center gap-2 shadow-sm"
                                                >
                                                    <Receipt size={14} /> Invoice
                                                </button>
                                            )}
                                            <button
                                                onClick={() => setSelectedOrder(order)}
                                                className="px-6 py-2 bg-gray-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black shadow-lg shadow-gray-200 transition-all"
                                            >
                                                Track Shipment
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-5 sm:p-6">
                                    <div className="space-y-4 mb-6">
                                        {order.items.map((item, idx) => (
                                            <div key={idx} className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 bg-gray-50/30 rounded-2xl border border-gray-50 hover:bg-white hover:border-blue-100 transition-all group">
                                                <div className="flex items-center gap-4 flex-1">
                                                    <div className="h-14 w-14 bg-white border border-gray-100 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden">
                                                        <OrderProductImage src={item.image_url} alt={item.product_name} />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <p className="font-black text-gray-900 text-sm truncate">{item.product_name}</p>
                                                            {parseFloat(item.price) === 0 && (
                                                                <span className="bg-amber-100 text-amber-700 text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-wider border border-amber-200 shadow-sm animate-pulse-subtle">
                                                                    FREE GIFT
                                                                </span>
                                                            )}
                                                        </div>
                                                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
                                                            {item.quantity} Unit{item.quantity > 1 ? 's' : ''} • {parseFloat(item.price) === 0 ? 'BONUS ITEM' : `₹${parseFloat(item.price).toLocaleString()}`}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="flex items-center justify-between sm:justify-end gap-4 w-full sm:w-auto border-t sm:border-t-0 pt-3 sm:pt-0">
                                                    <div className="text-right min-w-[80px]">
                                                        <div className={`font-black ${parseFloat(item.price) === 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                                                            {parseFloat(item.price) === 0 ? 'FREE' : `₹${parseFloat(item.price * item.quantity).toLocaleString()}`}
                                                        </div>
                                                        <p className="text-[9px] text-gray-400 font-bold uppercase tracking-tighter">Amount</p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {(order.status === 'Shipped' || order.status === 'Delivered') && order.tracking_number && (
                                        <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 flex gap-4 items-center animate-fadeIn">
                                            <div className="h-10 w-10 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600 shadow-sm">
                                                <Truck size={20} />
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest mb-0.5">Shipping Status</p>
                                                <p className="text-sm font-black text-blue-900">Shipped via {order.courier_name}</p>
                                                <p className="text-xs text-blue-700 font-bold">Tracking: <span className="select-all">{order.tracking_number}</span></p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-4 pt-4 border-t border-gray-50 flex justify-between items-center">
                                        <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Fulfillment By</div>
                                        <div className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider ${order.fulfillment_source === 'franchise' ? 'bg-orange-50 text-orange-600' : 'bg-blue-50 text-blue-600'}`}>
                                            {order.fulfillment_source === 'franchise' ? '🏪 Local Franchise' : '🏢 Central Admin'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                        <Package size={48} className="text-gray-300 mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-gray-900">No orders yet</h3>
                        <p className="text-gray-500">You haven't purchased any products yet.</p>
                    </div>
                )}
                {/* Tracking Modal */}
                {selectedOrder && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md animate-fadeIn">
                        <div className="bg-white rounded-[2rem] w-full max-w-lg shadow-2xl overflow-hidden animate-slideUp border border-white/20">
                            <div className="p-6 border-b flex justify-between items-center bg-gray-50/80 backdrop-blur-sm">
                                <div>
                                    <h3 className="font-black text-xl text-gray-900 tracking-tight">
                                        {selectedOrder.order_count > 1 ? 'Batch Shipment Track' : 'Track Order'}
                                    </h3>
                                    <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em]">
                                        {selectedOrder.order_count > 1 ? `${selectedOrder.order_count} Packages Combined` : selectedOrder.order_unique_id}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    className="p-2.5 bg-white hover:bg-gray-100 rounded-2xl transition-all shadow-sm border border-gray-100"
                                >
                                    <X className="text-gray-500" size={20} />
                                </button>
                            </div>

                            <div className="p-8 max-h-[70vh] overflow-y-auto custom-scrollbar">
                                {selectedOrder.order_count > 1 && (
                                    <div className="mb-8 space-y-3">
                                        <div className="flex items-center gap-2 mb-4">
                                            <div className="h-1 text-blue-600 bg-blue-600 rounded-full flex-1" />
                                            <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">Package Details</span>
                                            <div className="h-1 text-gray-100 bg-gray-100 rounded-full flex-1" />
                                        </div>
                                        {selectedOrder.original_orders.map(subOrder => (
                                            <div key={subOrder.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-2xl border border-gray-100">
                                                <div className="font-black text-xs text-gray-700">{subOrder.order_unique_id}</div>
                                                <span className={`px-2 py-0.5 rounded-lg text-[9px] font-black uppercase tracking-tighter border ${getStatusColor(subOrder.status)}`}>
                                                    {subOrder.status}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                <StatusTimeline currentStatus={selectedOrder.status} />

                                {(selectedOrder.status === 'Shipped' || selectedOrder.status === 'Delivered') && selectedOrder.tracking_number && (
                                    <div className="mt-10 pt-8 border-t border-gray-100">
                                        <div className="bg-blue-600 rounded-3xl p-6 text-white shadow-xl shadow-blue-100 relative overflow-hidden group">
                                            <div className="absolute -right-4 -top-4 w-24 h-24 bg-white/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700" />
                                            <div className="relative z-10 flex gap-4">
                                                <div className="p-3 bg-white/20 backdrop-blur-md rounded-2xl">
                                                    <Truck size={24} />
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-black text-blue-100 uppercase tracking-widest mb-1 opacity-80">Courier Partner</p>
                                                    <p className="text-lg font-black">{selectedOrder.courier_name}</p>
                                                    <div className="mt-3 py-1.5 px-3 bg-white/10 rounded-xl inline-flex items-center gap-2">
                                                        <span className="text-[10px] font-black text-blue-100 uppercase tracking-widest opacity-80">ID:</span>
                                                        <span className="text-sm font-black select-all">{selectedOrder.tracking_number}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="p-6 bg-gray-50/80 backdrop-blur-sm border-t flex gap-3">
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    className="flex-1 py-4 bg-gray-900 text-white rounded-[1.25rem] text-sm font-black uppercase tracking-widest hover:bg-black shadow-xl shadow-gray-200 transition-all hover:-translate-y-0.5"
                                >
                                    Finish Tracking
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {/* Invoice View Modal */}
            {viewInvoiceOrderId && (
                <InvoiceView
                    referenceId={viewInvoiceOrderId}
                    invoiceType="product"
                    onClose={() => setViewInvoiceOrderId(null)}
                    isAdmin={false}
                />
            )}
            <style>{`
                @keyframes pulse-subtle {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.85; transform: scale(1.05); }
                }
                .animate-pulse-subtle {
                    animation: pulse-subtle 2s ease-in-out infinite;
                }
            `}</style>
        </Layout >
    );
};

export default ProductOrders;


